/** Bookings (PRD M3): shared shapes and rules. */
export const BOOKING_STATUSES = ['C', 'CC', 'X'];
/** The team's two-step confirmation. Empty = not contacted yet. */
export const STATUS_LABELS = {
    none: 'Not contacted',
    C: 'C · contacted, pickup to confirm',
    CC: 'CC · confirmed',
    X: 'X · cancelled',
};
/** Office markers (2026 flags without Contacted/Confirmed, now status C/CC). */
export const FLAGS = [
    { key: 'warning', label: 'Attention', color: '#f4a800' },
    { key: 'problem', label: 'Problem', color: '#c0392b' },
    { key: 'info', label: 'Information', color: '#3b82f6' },
    { key: 'question', label: 'Question', color: '#a855f7' },
    { key: 'featured', label: 'Featured', color: '#e77a71' },
    { key: 'vip', label: 'VIP', color: '#be123c' },
    { key: 'no_show', label: 'No-show', color: '#7d3c98' },
    { key: 'cruise', label: 'Cruise', color: '#0e9fbf' },
];
/** Fields the team can write. */
export const BOOKING_EDITABLE = [
    'date', 'tourId', 'pickupTime', 'paxAdults', 'paxChildren', 'boosters', 'babySeats', 'clientName', 'clientNameLatin', 'phone', 'email',
    'language', 'hotelId', 'hotelName', 'address', 'status', 'flag', 'cashReminder', 'notes', 'brandId', 'channelId',
    'agencyId', 'agencyRef',
    'transferDirection', 'transportRef', 'expectedTime', 'expectedChecked', 'pickupPlace', 'pickupAddress', 'dropoffPlace', 'dropoffAddress', 'groupRef',
];
export const BOOKING_FIELD_LABELS = {
    date: 'Date', tourId: 'Tour', pickupTime: 'Pickup time', paxAdults: 'Adults', paxChildren: 'Children', boosters: 'Boosters',
    babySeats: 'Baby seats', clientName: 'Client', clientNameLatin: 'Name in Latin letters', phone: 'Phone', email: 'Email', language: 'Language', hotelId: 'Hotel',
    hotelName: 'Hotel (as written)', address: 'Address', status: 'Status', flag: 'Flag', cashReminder: 'Cash to collect',
    notes: 'Notes', brandId: 'Brand', channelId: 'Channel', agencyId: 'Agency', agencyRef: 'Agency reference',
    transferDirection: 'Direction', transportRef: 'Flight or train', expectedTime: 'Expected time', expectedChecked: 'Time checked',
    pickupPlace: 'Pickup place', pickupAddress: 'Pickup address', dropoffPlace: 'Drop-off place', dropoffAddress: 'Drop-off address', groupRef: 'Group',
};
export const TRANSFER_DIRECTIONS = ['arrival', 'departure', 'intercity'];
export const TRANSFER_DIRECTION_LABELS = { arrival: 'Arrival', departure: 'Departure', intercity: 'Intercity' };
/** Direction of a transfer tour, read from its code or name (TR-AP-NI arrival, TR-NI-AP departure, TR-NI-MA intercity). */
export function transferDirectionOf(tour) {
    const code = tour.code.toUpperCase();
    const name = tour.name ?? '';
    if (/^TR-(AP|GA)/.test(code) || code.includes('NAER'))
        return 'arrival';
    if (/-(AP|GA)$/.test(code))
        return 'departure';
    if (/to [^,]*(airport|gare|station)\s*$/i.test(name))
        return 'departure';
    if (/from [^,]*(airport|gare|station)/i.test(name))
        return 'arrival';
    return 'intercity';
}
/** The time a transfer actually runs: the checked expected time when there is one, else the official time. */
export const transferTime = (b) => b.expectedTime || b.pickupTime;
export const MISSING_LABELS = {
    tour: 'No tour',
    pickup: 'No pickup time',
    phone: 'No phone',
    address: 'No hotel or address',
    client: 'No client name',
    route: 'No pickup or drop-off place',
};
/** What is still missing to run this booking. Cancelled bookings miss nothing. */
export function missingInfo(b) {
    if (b.status === 'X')
        return [];
    const out = [];
    if (!b.tourId)
        out.push('tour');
    if (!b.pickupTime)
        out.push('pickup');
    if (!b.phone || !b.phone.trim())
        out.push('phone');
    const has = (...v) => v.some((x) => (x ?? '').trim());
    if (b.transferDirection) {
        // A transfer needs where to pick up and where to drop off (the hotel is one of the two).
        if (!has(b.pickupPlace, b.pickupAddress) || !has(b.dropoffPlace, b.dropoffAddress))
            out.push('route');
    }
    else if (!b.hotelId && !(b.hotelName ?? '').trim() && !(b.address ?? '').trim())
        out.push('address');
    if (!b.clientName.trim())
        out.push('client');
    return out;
}
/** The name the screens and the BDR show: the Latin-letter version when there is one. */
export const displayName = (b) => b.clientNameLatin || b.clientName;
/** True when a name has letters of another alphabet than the Latin one (accents are Latin). */
export const hasOtherAlphabet = (name) => !!name && /[^\p{Script=Latin}\p{Script=Common}\p{Script=Inherited}]/u.test(name);
//# sourceMappingURL=bookings.js.map